import { createApp } from 'vue';
import './plugins/assets';
import * as Sentry from '@sentry/vue';
import { createGtag } from "vue-gtag";
// import 'default-passive-events';
// import { InstallCodeMirror } from 'codemirror-editor-vue3';
import { router } from '@/router';
import { setupDayjs, setupIconifyOffline, setupLoading, setupNProgress } from './plugins';
import { setupStore } from './store';
import { setupRouter } from './router';
import { setupI18n } from './locales';
import App from './App.vue';

const SENTRY_DSN = import.meta.env.VITE_VUE_APP_SENTRY_DSN;
const GTAG_ID = import.meta.env.VITE_VUE_APP_GTAG_ID;

const isViteEnvironment = typeof import.meta !== 'undefined';

let IS_SENTRY_ENABLD = false;
if (isViteEnvironment && import.meta.env.VITE_BACKEND_ENV === 'local') {
    IS_SENTRY_ENABLD = false;
} else {
    IS_SENTRY_ENABLD = true;
}

const gtag = createGtag({
    tagId: GTAG_ID
})

async function setupApp() {
    setupLoading();

    setupNProgress();

    setupIconifyOffline();

    setupDayjs();

    const app = createApp(App);

    setupStore(app);

    await setupRouter(app);

    setupI18n(app);
    Sentry.init({
        app,
        enabled: IS_SENTRY_ENABLD,
        dsn: SENTRY_DSN,
        sampleRate: 0.05,
        integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
        // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
        tracePropagationTargets: [/^https:\/\/chat\.avalai\.org/, /^https:\/\/chat\.avalai\.ir/],

        ignoreErrors: [
            'Error in verifyRecaptcha:',
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications.',
            'Cannot start recording due to current state',
            "Failed to execute 'sendBeacon' on 'Navigator'",
            'top.GLOBALS',
            'originalCreateNotification',
            'canvas.contentDocument',
            'MyApp_RemoveAllHighlights',
            'http://tt.epicplay.com',
            "Can't find variable: ZiteReader",
            'jigsaw is not defined',
            'ComboSearch is not defined',
            'http://loading.retry.widdit.com/',
            'atomicFindClose',
            'fb_xd_fragment',
            'bmi_SafeAddOnload',
            'EBCallBackMessageReceived',
            'conduitPage',
            'Script error.',
            '_avast_submit'
        ],

        denyUrls: [
            // Google Adsense
            /pagead\/js/i,
            // Facebook flakiness
            /graph\.facebook\.com/i,
            // Facebook blocked
            /connect\.facebook\.net\/en_US\/all\.js/i,
            // Woopra flakiness
            /eatdifferent\.com\.woopra-ns\.com/i,
            /static\.woopra\.com\/js\/woopra\.js/i,
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i,
            // Other plugins
            /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
            /webappstoolbarba\.texthelp\.com\//i,
            /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
        ],
        // Performance Monitoring
        tracesSampleRate: 0.01,
        // Session Replay
        replaysSessionSampleRate: 0.001,
        replaysOnErrorSampleRate: 0.01,
        beforeSend: (event: any) => {
            if (window.location.hostname === 'localhost' || window.location.hostname === 'dev.local') {
                return null;
            }
            return event;
        }
    });
    app.use(gtag)
    //   app.use(InstallCodeMirror);

    app.mount('#app');
}

setupApp();
